/* Main feed page: header with item count, filter tabs, and infinite-scroll feed list. */

import { Fragment, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
	usePaginatedQuery,
	useQuery,
	useMutation,
} from "convex/react";
import { api } from "../../../convex/_generated/api";
import { openFeedItem } from "@/lib/openFeedItem";
import { Tabs } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, HelpCircle } from "lucide-react";
import { ContentFlowInfo, AIFeaturesInfo } from "@/components/feature-info";
import { CompactFeedUpdateButton } from "@/components/feed/CompactFeedUpdateButton";
import { useFeedUpdateProgress } from "@/hooks/useFeedUpdateProgress";
import { SwipeModeButton } from "@/components/feed/SwipeModeButton";
import { FeedCleanerDialog } from "@/components/feed-cleaner/FeedCleanerDialog";
import { RoutePath } from "@/routePaths";
import { FeedEmptyStats } from "@/components/feed/FeedEmptyStats";
import { AISummaryBanner } from "@/components/ai-summary/AISummaryBanner";
import { FeedItemRow } from "@/components/feed/FeedItemRow";
import { FeedItemMobileCard } from "@/components/feed/FeedItemMobileCard";
import { BookItemRow } from "@/components/books/BookItemRow";
import { BookItemMobileCard } from "@/components/books/BookItemMobileCard";
import { MovieItemRow } from "@/components/movies/MovieItemRow";
import { MovieItemMobileCard } from "@/components/movies/MovieItemMobileCard";
import { FeedItemRowSkeleton } from "@/components/feed/FeedItemRowSkeleton";

import { useUserSettings } from "@/hooks/useUserSettings";
import type { Id } from "../../../convex/_generated/dataModel";
import type { FeedItemData } from "@/components/feed/FeedItemRow";
import { useFeedListKeyboard } from "@/hooks/useFeedListKeyboard";
import { useFeedItems } from "@/hooks/useFeedItems";
import { useLoadMore } from "@/hooks/useLoadMore";
import { ContentTypeTabs } from "@/components/feed/ContentTypeTabs";
import { AddSourceDialog } from "@/components/sources/AddSourceDialog";
import { NoSourcesEmptyState } from "@/components/sources/NoSourcesEmptyState";
import { useGroupedFeedItems } from "@/hooks/useGroupedFeedItems";
import { useSectionGroupings } from "@/hooks/useSectionGroupings";
import { useStickyHeaderHeight } from "@/hooks/useStickyHeaderHeight";
import { useFeatureTracking } from "@/hooks/useFeatureTracking";
import { castFeedItems, type RawItem } from "@/lib/castFeedItems";
import { useSourceInfoDialog } from "@/hooks/useSourceInfoDialog";
import { SourceInfoDialog } from "@/components/sources/SourceInfoDialog";

const LOAD_MORE_COUNT = 20;
const TAB_INITIAL_NUM_ITEMS = 30;
const PRELOAD_THRESHOLD = 10;

function getTimeUntilMidnight(): string {
	const now = new Date();
	const midnight = new Date(now);
	midnight.setHours(24, 0, 0, 0);
	const diffMs = midnight.getTime() - now.getTime();
	const hours = Math.floor(diffMs / (1000 * 60 * 60));
	const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
	if (hours > 0) return `${hours}h ${minutes}m`;
	return `${minutes}m`;
}

export function FeedPage() {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState("all");
	const [filterTag, setFilterTag] = useState<string | null>(null);
	const [filterTopic, setFilterTopic] = useState<string | null>(null);
	const { hideProvocativeContent } = useUserSettings();
	const [cleanerOpen, setCleanerOpen] = useState(false);
	const [infoOpen, setInfoOpen] = useState(false);
	const [aiInfoOpen, setAiInfoOpen] = useState(false);
	const { trackFeature } = useFeatureTracking();
	const hasTrackedFilter = useRef(false);

	// Track when user changes content type filter (not the default "all" tab)
	useEffect(() => {
		if (activeTab !== "all" && !hasTrackedFilter.current) {
			hasTrackedFilter.current = true;
			trackFeature("filter_used");
		}
	}, [activeTab, trackFeature]);

	const isProvocativeTab = activeTab === "provocative";
	const isForYouTab = activeTab === "for_you";
	const isAllTab = activeTab === "all";
	const { sectionsEnabled, sectionGroupings } = useSectionGroupings();
	const useSectionPagination =
		sectionsEnabled && sectionGroupings.length > 0 && isAllTab && !filterTag && !filterTopic;

	const sourceCount = useQuery(api.sources.count);
	const feedCtx = useFeedItems();
	const sortOrder = feedCtx.sortOrder;
	const setSortOrder = feedCtx.setSortOrder;
	const forYouData = useQuery(api.analytics.getForYouSourceIds);
	const allCounts = useQuery(api.feedItems.getAllCounts);
	const countsByType = allCounts?.feed;
	const queueCountsByType = allCounts?.queue;
	const availableTags = useQuery(api.tags.getAvailableTags);
	const availableTopics = useQuery(api.tags.getAiTopics);
	const backendPlanIds = useQuery(api.dailyPlans.get);
	const addItemsToPlan = useMutation(api.dailyPlans.addItems);
	const setSkipProvocationCheck = useMutation(api.sources.setSkipProvocationCheck);

	const planItemIdSet = useMemo(
		() => new Set<string>(backendPlanIds ?? []),
		[backendPlanIds],
	);

	const handleAddToPlan = useCallback(
		(ids: string[]) => {
			for (const id of ids) {
				feedCtx.queueItem(id as Id<"feedItems">);
			}
			addItemsToPlan({ itemIds: ids });
		},
		[feedCtx, addItemsToPlan],
	);

	// Paginated query for provocative tab
	const provocativeQuery = usePaginatedQuery(
		api.feedItems.list,
		isProvocativeTab
			? { showOnlyProvocative: true, sortOrder, ...(filterTag ? { filterTag } : {}) }
			: "skip",
		{ initialNumItems: TAB_INITIAL_NUM_ITEMS },
	);

	// Paginated query for "all" tab when a tag filter is active
	const allWithTagQuery = usePaginatedQuery(
		api.feedItems.list,
		isAllTab && filterTag
			? {
					contentType: "all",
					filterTag,
					sortOrder,
					hideProvocative: hideProvocativeContent,
				}
			: "skip",
		{ initialNumItems: TAB_INITIAL_NUM_ITEMS },
	);

	// Paginated query for topic filter
	const topicFilterQuery = usePaginatedQuery(
		api.feedItems.list,
		isAllTab && filterTopic && !filterTag
			? {
					contentType: "all",
					filterTopic,
					sortOrder,
					hideProvocative: hideProvocativeContent,
				}
			: "skip",
		{ initialNumItems: TAB_INITIAL_NUM_ITEMS },
	);

	// Paginated query for "For You" tab
	const forYouQuery = usePaginatedQuery(
		api.feedItems.list,
		isForYouTab && forYouData?.sourceIds.length
			? {
					contentType: "all",
					filterSourceIds: forYouData.sourceIds,
					sortOrder,
					hideProvocative: hideProvocativeContent,
				}
			: "skip",
		{ initialNumItems: TAB_INITIAL_NUM_ITEMS },
	);

	// Paginated query for specific content type tabs
	const tabQuery = usePaginatedQuery(
		api.feedItems.list,
		!isProvocativeTab && !isForYouTab && !isAllTab
			? {
					contentType: activeTab,
					sortOrder,
					hideProvocative: hideProvocativeContent,
					...(filterTag ? { filterTag } : {}),
				}
			: "skip",
		{ initialNumItems: TAB_INITIAL_NUM_ITEMS },
	);

	let results: FeedItemData[];
	let isFirstLoading: boolean;
	let hasMore: boolean;
	let isLoadingMore: boolean;
	let loadMore: (count: number) => void;

	if (isProvocativeTab) {
		results = castFeedItems(provocativeQuery.results as unknown as RawItem[]);
		isFirstLoading = provocativeQuery.status === "LoadingFirstPage";
		hasMore = provocativeQuery.status === "CanLoadMore";
		isLoadingMore = provocativeQuery.status === "LoadingMore";
		loadMore = provocativeQuery.loadMore;
	} else if (isForYouTab) {
		const forYouItems = castFeedItems(forYouQuery.results as unknown as RawItem[]);
		results = forYouData?.reasons
			? forYouItems.map((item) => {
					const reason = forYouData.reasons[item.sourceId as string];
					return reason
						? { ...item, forYouReason: reason.label, forYouReasonDetail: reason.detail }
						: item;
				})
			: forYouItems;
		isFirstLoading = forYouQuery.status === "LoadingFirstPage";
		hasMore = false;
		isLoadingMore = false;
		loadMore = () => {};
	} else if (isAllTab && filterTopic && !filterTag) {
		results = castFeedItems(topicFilterQuery.results as unknown as RawItem[]);
		isFirstLoading = topicFilterQuery.status === "LoadingFirstPage";
		hasMore = topicFilterQuery.status === "CanLoadMore";
		isLoadingMore = topicFilterQuery.status === "LoadingMore";
		loadMore = topicFilterQuery.loadMore;
	} else if (isAllTab && filterTag) {
		results = castFeedItems(allWithTagQuery.results as unknown as RawItem[]);
		isFirstLoading = allWithTagQuery.status === "LoadingFirstPage";
		hasMore = allWithTagQuery.status === "CanLoadMore";
		isLoadingMore = allWithTagQuery.status === "LoadingMore";
		loadMore = allWithTagQuery.loadMore;
	} else if (isAllTab) {
		results = feedCtx.items;
		isFirstLoading = feedCtx.isFirstLoading;
		hasMore = feedCtx.hasMore;
		isLoadingMore = feedCtx.isLoadingMore;
		loadMore = feedCtx.loadMore;
	} else {
		results = castFeedItems(tabQuery.results as unknown as RawItem[]);
		isFirstLoading = tabQuery.status === "LoadingFirstPage";
		hasMore = tabQuery.status === "CanLoadMore";
		isLoadingMore = tabQuery.status === "LoadingMore";
		loadMore = tabQuery.loadMore;
	}

	// Group items by topic section order from settings (client-side reorder, O(n)).
	const { groupedItems, getSectionName } = useGroupedFeedItems(results);
	const displayItems = groupedItems;

	const loadMoreCount = useSectionPagination
		? TAB_INITIAL_NUM_ITEMS
		: LOAD_MORE_COUNT;
	const handleLoadMore = useCallback(() => {
		loadMore(loadMoreCount);
	}, [loadMore, loadMoreCount]);

	const sentinelRef = useLoadMore({ loadMore: handleLoadMore, hasMore });

	const feedUpdate = useFeedUpdateProgress();
	const feedListRef = useFeedListKeyboard();
	const { headerRef, headerHeight } = useStickyHeaderHeight();
	const { sourceInfoProps, handleSourceClick } = useSourceInfoDialog();

	return (
		<div className="flex flex-col">
			<div className="w-full md:max-w-[980px]">
				<Tabs value={activeTab} onValueChange={setActiveTab} className="pb-6">
					<div ref={headerRef} className="sticky top-0 z-20 bg-background">
						<div className="flex items-center justify-between px-4 md:px-6 pt-4 md:pt-6 pb-2">
							<div className="flex items-baseline gap-2">
								<h1 className="text-2xl md:text-3xl font-bold tracking-tight">
									Feed
								</h1>
								<button
									onClick={() => setInfoOpen(true)}
									className="text-muted-foreground/50 hover:text-muted-foreground transition-colors"
									aria-label="How Feed works"
								>
									<HelpCircle className="size-4" />
								</button>
								<button
									onClick={() => setAiInfoOpen(true)}
									className="text-muted-foreground/50 hover:text-ai-accent transition-colors"
									aria-label="AI features guide"
								>
									<Sparkles className="size-4" />
								</button>
								<span className="text-sm text-muted-foreground">
									{(isProvocativeTab
										? feedCtx.provocativeCount
										: isAllTab
											? feedCtx.totalCount
											: countsByType?.[activeTab as keyof typeof countsByType]) ??
										"–"}{" "}
									items
								</span>
							</div>
							<div className="flex items-center gap-2 shrink-0">
								<div data-guide="update-feed-button">
									<CompactFeedUpdateButton
										state={feedUpdate.state}
										progress={feedUpdate.progress}
										currentIndex={feedUpdate.currentIndex}
										totalSources={feedUpdate.totalSources}
										timeUntilMidnight={getTimeUntilMidnight()}
										lastUpdatedAt={feedUpdate.lastUpdatedAt}
										onUpdate={feedUpdate.startUpdate}
										onCancel={feedUpdate.cancelUpdate}
									/>
								</div>
								<Button
									variant="outline"
									size="sm"
									className="gap-1.5"
									onClick={() => setCleanerOpen(true)}
								>
									<Sparkles className="size-4" />
									<span className="hidden sm:inline">Smart Clean</span>
								</Button>
								<div className="hidden md:block">
									<AddSourceDialog />
								</div>
								<SwipeModeButton
									variant="soft-cards"
									onClick={() => navigate(RoutePath.Swipe)}
								/>
							</div>
						</div>

						<div data-onboard="feed-filters">
						<ContentTypeTabs
							activeTab={activeTab}
							onActiveTabChange={setActiveTab}
							provocativeCount={
								hideProvocativeContent ? feedCtx.provocativeCount : undefined
							}
							showForYou={forYouData?.hasEnoughData}
							filterTag={filterTag}
							onFilterTagChange={setFilterTag}
							availableTags={availableTags}
							filterTopic={filterTopic}
							onFilterTopicChange={setFilterTopic}
							availableTopics={availableTopics}
							sortOrder={sortOrder}
							onSortOrderChange={setSortOrder}
						/>
						</div>
						{isForYouTab && (
							<p className="px-4 md:px-6 pt-2 text-xs text-muted-foreground">
								Based on your completion history
							</p>
						)}
					</div>

					<div className="pt-3">
						<AISummaryBanner
							feedItemCount={feedCtx.totalCount ?? 0}
							planItemIds={planItemIdSet}
							onAddToPlan={handleAddToPlan}
							onTagClick={setFilterTag}
						/>
					</div>

						<div ref={feedListRef} className="py-1 grid" data-onboard="feed-list">
							<div
								className={`col-start-1 row-start-1 transition-opacity duration-300 ${!isFirstLoading ? "opacity-0 pointer-events-none" : ""}`}
								aria-hidden={!isFirstLoading}
							>
								{Array.from({ length: 6 }, (_, i) => (
									<FeedItemRowSkeleton key={i} />
								))}
							</div>
							{!isFirstLoading && (
								<div className="col-start-1 row-start-1 animate-in fade-in duration-300 md:max-w-[896px] ">
									{displayItems.length === 0 && sourceCount === 0 ? (
										<NoSourcesEmptyState variant="cards" />
									) : displayItems.length === 0 ? (
										<div className="text-center text-muted-foreground py-12">
											<p>No items in your feed.</p>
											<FeedEmptyStats queueCount={queueCountsByType?.all} />
										</div>
									) : (
										displayItems.map((item, index) => {
											const sentinelIndex = displayItems.length - PRELOAD_THRESHOLD;
											const sectionName = getSectionName(item._id);
											const prevSectionName = index > 0 ? getSectionName(displayItems[index - 1]._id) : undefined;
											const showSectionHeader = sectionName && sectionName !== prevSectionName;
											const itemId = item._id as Id<"feedItems">;
											const callbacks = {
												onOpen: () => openFeedItem(item, navigate),
												onDelete: () => feedCtx.removeItem(itemId),
												onComplete: () => feedCtx.completeItem(itemId),
												onQueue: () => { trackFeature("queue_item"); feedCtx.queueItem(itemId); },
												onBacklog: () => { trackFeature("backlog_item"); feedCtx.backlogItem(itemId); },
												onToggleSkipProvocation: item.sourceId ? () => setSkipProvocationCheck({ id: item.sourceId as Id<"sources">, skip: !item.sourceSkipProvocationCheck }) : undefined,
											};
											const isBook = item.contentType === "book";
											const isMovie = item.contentType === "movie";
											const nextSectionName = index < displayItems.length - 1 ? getSectionName(displayItems[index + 1]._id) : undefined;
											const isLastInSection = index === displayItems.length - 1 || nextSectionName !== sectionName;
											const guideAttrs = index === 0 ? { "data-guide": "feed-item-actions" } : {};
											return (
												<Fragment key={item._id}>
													{showSectionHeader && (
														<div
															className="sticky z-10 bg-background flex items-center gap-3 px-4 md:px-6 pt-5 pb-2"
															style={{ top: headerHeight }}
														>
															<span className="text-xs font-semibold uppercase tracking-wider text-primary">{sectionName}</span>
															<div className="flex-1 border-t border-border" />
														</div>
													)}
													{index === sentinelIndex && (
														<div ref={sentinelRef} className="h-0" />
													)}
													<div className="hidden md:block" {...guideAttrs}>
														{isBook ? (
															<BookItemRow item={item} {...callbacks} />
														) : isMovie ? (
															<MovieItemRow item={item} {...callbacks} />
														) : (
															<FeedItemRow
																item={item}
																hideBorder={isLastInSection}
																{...callbacks}
																onTagClick={setFilterTag}
																onTopicClick={setFilterTopic}
																onSourceClick={handleSourceClick}
															/>
														)}
													</div>
													<div className="md:hidden">
														{isBook ? (
															<BookItemMobileCard item={item} {...callbacks} />
														) : isMovie ? (
															<MovieItemMobileCard item={item} {...callbacks} />
														) : (
															<FeedItemMobileCard
																item={item}
																{...callbacks}
																onTagClick={setFilterTag}
																onTopicClick={setFilterTopic}
																onSourceClick={handleSourceClick}
															/>
														)}
													</div>
												</Fragment>
											);
										})
									)}
									{isLoadingMore && (
										<div className="flex justify-center py-4">
											<Loader2 className="size-5 animate-spin text-muted-foreground" />
										</div>
									)}
								</div>
							)}
						</div>
					</Tabs>
				</div>
			<FeedCleanerDialog open={cleanerOpen} onOpenChange={setCleanerOpen} />
		<SourceInfoDialog {...sourceInfoProps} />
		<ContentFlowInfo open={infoOpen} onOpenChange={setInfoOpen} />
		<AIFeaturesInfo open={aiInfoOpen} onOpenChange={setAiInfoOpen} />
		</div>
	);
}
